import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';

import {
  interactiveHoverVariants,
  overlayVariants,
  slideInRightVariants,
  staggerContainerVariants,
  staggerItemVariants,
} from '@/animations';
import type { UserApi as User } from '@/api';
import { Button, LazyImage } from '@/components';
import { NAV_CONTENT } from '@/content';
import { Icon } from '@/utils/icons';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isAuthenticated: boolean;
  user: User | null;
  logout: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose,
  isAuthenticated,
  user,
  logout,
}) => {
  const location = useLocation();
  const navItems = NAV_CONTENT.items;
  const dialogRef = React.useRef<HTMLDivElement>(null);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path.split('?')[0]);
  };

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <motion.div
            variants={overlayVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            ref={dialogRef}
            variants={slideInRightVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile Navigation"
            className="shadow-premium absolute top-0 right-0 h-full w-72 overflow-x-hidden overflow-y-auto bg-white dark:bg-neutral-950">
            <div className="pointer-events-none absolute inset-0 z-0 h-full w-full overflow-hidden opacity-50">
              <div className="from-primary-100/30 absolute top-0 left-0 h-64 w-full bg-gradient-to-b to-transparent" />
              <div className="bg-primary-300/10 absolute top-[-10%] left-[-20%] h-[40%] w-[120%] rounded-full blur-[80px]" />
            </div>

            <div className="relative z-10 flex h-full flex-col p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-display text-2xl font-bold text-neutral-800 dark:text-neutral-100">
                  {NAV_CONTENT.mobile.menuLabel}
                </span>
                <motion.button
                  onClick={onClose}
                  whileHover="hover"
                  whileTap="tap"
                  variants={interactiveHoverVariants}
                  className="hover:bg-primary-50 rounded-2xl p-2 text-neutral-600 transition-all dark:text-neutral-400 dark:hover:bg-neutral-800"
                  aria-label="Close menu">
                  <Icon name="X" size={24} />
                </motion.button>
              </div>

              <nav aria-label="Mobile" className="flex-1 space-y-2">
                <motion.div
                  variants={staggerContainerVariants}
                  initial="initial"
                  animate="animate"
                  className="space-y-1">
                  {navItems.map(item => (
                    <motion.div key={item.label} variants={staggerItemVariants}>
                      {item.children ? (
                        <div className="space-y-1">
                          <span className="font-body block p-2 text-sm font-bold tracking-wider text-neutral-400 uppercase">
                            {item.label}
                          </span>
                          <div className="space-y-1 pl-6">
                            {item.children.map(child => (
                              <Link
                                key={child.label}
                                to={child.path}
                                onClick={onClose}
                                className="font-body hover:bg-primary-50 hover:text-primary-600 block rounded-xl p-2 text-base font-semibold text-neutral-700 transition-all dark:text-neutral-300 dark:hover:bg-neutral-800">
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <Link
                          to={item.path}
                          onClick={onClose}
                          className={`font-body block rounded-2xl p-2 text-base font-bold transition-all ${
                            isActive(item.path)
                              ? 'bg-primary-50 text-primary-600 shadow-primary-100/50 shadow-sm'
                              : 'hover:bg-primary-50/50 hover:text-primary-600 text-neutral-700'
                          }`}>
                          {item.label}
                        </Link>
                      )}
                    </motion.div>
                  ))}
                </motion.div>
              </nav>

              <div className="border-primary-100 mt-auto space-y-3 border-t pt-2">
                {isAuthenticated ? (
                  <>
                    <div className="mb-4 flex items-center gap-3 rounded-2xl bg-neutral-50 p-4 ring-1 ring-neutral-100">
                      <div className="from-primary-100 to-primary-200 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br shadow-sm">
                        {user?.avatar ? (
                          <LazyImage
                            src={user.avatar}
                            alt={user.name}
                            className="h-full w-full"
                            imgClassName="rounded-2xl object-cover"
                          />
                        ) : (
                          <Icon name="User" size={24} className="text-primary-600" />
                        )}
                      </div>
                      <div className="overflow-hidden">
                        <p className="font-body truncate text-sm font-bold text-neutral-800">
                          {user?.name}
                        </p>
                        <p className="font-body truncate text-xs text-neutral-500">{user?.email}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        as="link"
                        to="/account"
                        variant="secondary"
                        size="sm"
                        fullWidth
                        onClick={onClose}>
                        {NAV_CONTENT.userMenu.myAccount}
                      </Button>
                      <button
                        onClick={() => {
                          logout();
                          onClose();
                        }}
                        type="button"
                        className="rounded-2xl border-2 border-red-100 py-3 text-sm font-bold text-red-500 transition-all hover:border-red-200 hover:bg-red-50">
                        {NAV_CONTENT.userMenu.signOut}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    <Button as="link" to="/login" variant="primary" fullWidth onClick={onClose}>
                      {NAV_CONTENT.actions.signIn.label}
                    </Button>
                    <Button
                      as="link"
                      to="/register"
                      variant="secondary"
                      fullWidth
                      onClick={onClose}>
                      {NAV_CONTENT.mobile.createAccount}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default React.memo(MobileMenu);
